export interface AnalyticsOptions {
  apiUrl?: string;
  flushIntervalMs?: number;
  flushAt?: number;
  storageKey?: string;
  heartbeatIntervalMs?: number;
  sessionTimeoutMs?: number;
  disablePresence?: boolean;
  headers?: Record<string, string>;
}

export interface EventContext {
  [key: string]: unknown;
}

export interface EventPayload {
  event: string;
  userId?: string;
  anonymousId?: string;
  sessionId?: string;
  orgId?: string;
  properties?: Record<string, unknown>;
  context?: EventContext;
  traits?: Record<string, unknown>;
  timestamp: string;
}

interface IdentityState {
  userId?: string;
  traits?: Record<string, unknown>;
  orgId?: string;
}

const DEFAULT_OPTIONS: Required<Omit<AnalyticsOptions, "headers">> = {
  apiUrl: "http://localhost:8080",
  flushIntervalMs: 5000,
  flushAt: 20,
  storageKey: "pixby.analytics.queue",
  heartbeatIntervalMs: 15000,
  sessionTimeoutMs: 30 * 60 * 1000,
  disablePresence: false
};

const IDENTITY_KEY = "pixby.analytics.identity";
const ANON_KEY = "pixby.analytics.anonymous_id";
const SESSION_KEY = "pixby.analytics.session_id";
const SESSION_STATE_KEY = "pixby.analytics.session_state";
const PRESENCE_EVENT = "presence_heartbeat";

interface SessionState {
  id: string;
  startedAt: string;
  lastActivityAt: string;
}

class AnalyticsClient {
  private options: Required<Omit<AnalyticsOptions, "headers">> & { headers: Record<string, string> };
  private projectKey = "";
  private queue: EventPayload[] = [];
  private flushTimer?: number;
  private heartbeatTimer?: number;
  private identity: IdentityState = {};
  private anonymousId = "";
  private session: SessionState = { id: "", startedAt: "", lastActivityAt: "" };
  private isFlushing = false;
  private listenersBound = false;

  constructor() {
    this.options = {
      ...DEFAULT_OPTIONS,
      headers: {}
    };
  }

  init(projectKey: string, options: AnalyticsOptions = {}) {
    this.projectKey = projectKey;
    this.options = {
      apiUrl: options.apiUrl ?? DEFAULT_OPTIONS.apiUrl,
      flushIntervalMs: options.flushIntervalMs ?? DEFAULT_OPTIONS.flushIntervalMs,
      flushAt: options.flushAt ?? DEFAULT_OPTIONS.flushAt,
      storageKey: options.storageKey ?? DEFAULT_OPTIONS.storageKey,
      heartbeatIntervalMs: options.heartbeatIntervalMs ?? DEFAULT_OPTIONS.heartbeatIntervalMs,
      sessionTimeoutMs: options.sessionTimeoutMs ?? DEFAULT_OPTIONS.sessionTimeoutMs,
      disablePresence: options.disablePresence ?? DEFAULT_OPTIONS.disablePresence,
      headers: options.headers ?? {}
    };

    this.queue = this.loadQueue();
    this.identity = this.loadIdentity();
    this.anonymousId = this.loadAnonymousId();
    this.session = this.loadSessionState();
    this.ensureSession();

    this.startFlushLoop();
    this.startPresenceLoop();
    this.bindLifecycleFlush();
  }

  identify(userId: string, traits: Record<string, unknown> = {}) {
    this.identity = {
      ...this.identity,
      userId,
      orgId: typeof traits.orgId === "string" ? traits.orgId : this.identity.orgId,
      traits
    };

    this.persistIdentity();
  }

  track(event: string, properties: Record<string, unknown> = {}, context: EventContext = {}) {
    this.assertReady();
    const session = this.ensureSession();

    const payload: EventPayload = {
      event,
      userId: this.identity.userId,
      anonymousId: this.anonymousId,
      sessionId: session.id,
      orgId: this.identity.orgId,
      properties,
      context: {
        ...this.detectContext(),
        sessionId: session.id,
        sessionStartedAt: session.startedAt,
        ...context
      },
      traits: this.identity.traits,
      timestamp: new Date().toISOString()
    };

    this.queue.push(payload);
    this.persistQueue();

    if (this.queue.length >= this.options.flushAt) {
      void this.flush();
    }
  }

  async heartbeat() {
    this.assertReady();
    if (this.options.disablePresence || typeof document === "undefined" || document.visibilityState === "hidden") {
      return;
    }
    const session = this.ensureSession();

    const payload: EventPayload = {
      event: PRESENCE_EVENT,
      userId: this.identity.userId,
      anonymousId: this.anonymousId,
      sessionId: session.id,
      orgId: this.identity.orgId,
      properties: {
        session_id: session.id,
        source: "sdk_presence"
      },
      context: {
        ...this.detectContext(),
        sessionId: session.id,
        sessionStartedAt: session.startedAt,
        kind: "presence"
      },
      traits: this.identity.traits,
      timestamp: new Date().toISOString()
    };

    try {
      await this.sendEvent(payload);
    } catch {
      // Presence is best-effort only.
    }
  }

  async flush() {
    this.assertReady();
    if (this.isFlushing || this.queue.length === 0) {
      return;
    }

    this.isFlushing = true;
    const pending = [...this.queue];

    try {
      for (const event of pending) {
        await this.sendEvent(event);
      }
      this.queue = [];
      this.persistQueue();
    } catch {
      // Keep queue for retry. Failures are expected while offline.
    } finally {
      this.isFlushing = false;
    }
  }

  private async sendEvent(event: EventPayload) {
    const payload = JSON.stringify(event);
    const endpoint = `${this.options.apiUrl.replace(/\/$/, "")}/events`;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Project-Key": this.projectKey,
        ...this.options.headers
      },
      body: payload,
      keepalive: true
    });

    if (!response.ok) {
      throw new Error(`event send failed: ${response.status}`);
    }
  }

  private startFlushLoop() {
    if (typeof window === "undefined") {
      return;
    }

    if (this.flushTimer) {
      window.clearInterval(this.flushTimer);
    }

    this.flushTimer = window.setInterval(() => {
      void this.flush();
    }, this.options.flushIntervalMs);
  }

  private startPresenceLoop() {
    if (typeof window === "undefined" || this.options.disablePresence) {
      return;
    }

    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
    }

    void this.heartbeat();

    this.heartbeatTimer = window.setInterval(() => {
      void this.heartbeat();
    }, this.options.heartbeatIntervalMs);
  }

  private bindLifecycleFlush() {
    if (typeof window === "undefined" || this.listenersBound) {
      return;
    }

    this.listenersBound = true;
    window.addEventListener("online", () => void this.flush(), { passive: true });
    window.addEventListener("beforeunload", () => {
      void this.flush();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        void this.heartbeat();
      }
      if (document.visibilityState === "hidden") {
        void this.flush();
      }
    });
  }

  private detectContext(): EventContext {
    if (typeof navigator === "undefined") {
      return {};
    }

    const ua = navigator.userAgent.toLowerCase();
    return {
      browser: inferBrowser(ua),
      device: /mobile/.test(ua) ? "mobile" : "desktop",
      os: inferOS(ua),
      path: typeof location !== "undefined" ? location.pathname : undefined,
      referrer: inferReferrer()
    };
  }

  private loadQueue() {
    return readJSON<EventPayload[]>(this.options.storageKey) ?? [];
  }

  private loadIdentity() {
    return readJSON<IdentityState>(IDENTITY_KEY) ?? {};
  }

  private persistQueue() {
    writeJSON(this.options.storageKey, this.queue);
  }

  private persistIdentity() {
    writeJSON(IDENTITY_KEY, this.identity);
  }

  private loadAnonymousId() {
    const existing = readText(ANON_KEY);
    if (existing) {
      return existing;
    }

    const generated = generateId();
    writeText(ANON_KEY, generated);
    return generated;
  }

  private loadSessionState(): SessionState {
    const existing = readJSON<SessionState>(SESSION_STATE_KEY);
    if (existing?.id) {
      return existing;
    }

    const legacySessionId = readText(SESSION_KEY);
    if (legacySessionId) {
      const now = new Date().toISOString();
      const state = {
        id: legacySessionId,
        startedAt: now,
        lastActivityAt: now
      };
      writeJSON(SESSION_STATE_KEY, state);
      return state;
    }

    return this.createSessionState();
  }

  private assertReady() {
    if (!this.projectKey) {
      throw new Error("analytics.init(projectKey) must be called before use");
    }
  }

  private ensureSession() {
    const now = Date.now();
    const lastActivityAt = this.session.lastActivityAt ? Date.parse(this.session.lastActivityAt) : 0;
    const expired = !this.session.id || !lastActivityAt || now-lastActivityAt > this.options.sessionTimeoutMs;

    if (expired) {
      this.session = this.createSessionState(now);
    } else {
      this.session = {
        ...this.session,
        lastActivityAt: new Date(now).toISOString()
      };
      this.persistSessionState();
    }

    return this.session;
  }

  private createSessionState(now = Date.now()): SessionState {
    const isoNow = new Date(now).toISOString();
    const state = {
      id: generateId(),
      startedAt: isoNow,
      lastActivityAt: isoNow
    };
    this.session = state;
    this.persistSessionState();
    return state;
  }

  private persistSessionState() {
    writeJSON(SESSION_STATE_KEY, this.session);
    writeText(SESSION_KEY, this.session.id);
  }
}

function inferBrowser(ua: string) {
  if (ua.includes("firefox")) return "firefox";
  if (ua.includes("edg")) return "edge";
  if (ua.includes("chrome")) return "chrome";
  if (ua.includes("safari")) return "safari";
  return "unknown";
}

function inferOS(ua: string) {
  if (ua.includes("windows")) return "windows";
  if (ua.includes("mac os")) return "macos";
  if (ua.includes("android")) return "android";
  if (ua.includes("iphone") || ua.includes("ipad")) return "ios";
  if (ua.includes("linux")) return "linux";
  return "unknown";
}

function inferReferrer() {
  if (typeof document === "undefined" || !document.referrer) {
    return "direct";
  }

  try {
    const url = new URL(document.referrer);
    return url.hostname.replace(/^www\./, "") || "direct";
  } catch {
    return "direct";
  }
}

function readJSON<T>(key: string): T | null {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const raw = localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJSON(key: string, value: unknown) {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(key, JSON.stringify(value));
}

function readText(key: string): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage.getItem(key);
}

function writeText(key: string, value: string) {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(key, value);
}

function generateId() {
  const cryptoObject = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;

  if (cryptoObject && typeof cryptoObject.randomUUID === "function") {
    return cryptoObject.randomUUID();
  }

  if (cryptoObject && typeof cryptoObject.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoObject.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  const now = Date.now().toString(16);
  const random = Math.random().toString(16).slice(2).padEnd(16, "0");
  return `${now.slice(0, 8)}-${random.slice(0, 4)}-4${random.slice(4, 7)}-a${random.slice(7, 10)}-${random.slice(10, 22)}`;
}

const analytics = new AnalyticsClient();

export const init = analytics.init.bind(analytics);
export const identify = analytics.identify.bind(analytics);
export const track = analytics.track.bind(analytics);
export const flush = analytics.flush.bind(analytics);
export default analytics;
